import {
  ALL_ARTISTS_QUERY,
  ARTISTS_COLLECTION_PATH,
  PUZZLE_STATUSES,
  PuzzleStatusConstants,
} from 'app-constants';
import {
  collection,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
  WithFieldValue,
} from 'firebase/firestore';
import { deleteObject, ref as storageRef } from 'firebase/storage';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import { Status } from 'shared/enums/status';
import { useModalProvider } from 'context/ModalProviderContext';
import { getCombinedState, useLiveData } from 'hooks/useLiveData';
import { PuzzleDTO, PuzzleDTOSchema, RecursivePartial, VideoPuzzleDTO } from 'types';
import { ALL_CATEGORIES_QUERY } from 'app-constants/categories';
import { Option } from 'shared/Components/Select/Select';
import { useDocument, useForm } from 'hooks';
import { puzzleApprovedNotificationApi, puzzleRejectedNotificationApi, storage } from 'services';
import { firestore } from 'services/Firebase';
import { Button, Input, RejectionModal, SelectForm, Splash } from 'shared/Components';
import { SelectMultiForm } from 'shared/Components/Select/SelectMultiForm';
import { Box, FlexContainer, Image, Text } from 'shared/Typhography';
import { Container } from 'shared/Typhography/GridSystem';
import { strings } from 'strings';
import { EditPuzzleField, editPuzzleScreenSchema, isSchema } from 'validations';
import { theme } from 'theme';
import { getDailyPuzzleByPuzzleId } from 'services/api/dailyPuzzlesApi';
import { FlexColumn } from 'shared/Typhography/flex-box';
import { LinkButton } from 'shared/Typhography/LinkButton';

import { checkIfIsGotivaArtist } from '../CreatePuzzleScreen/utilities';
import usePremiumPuzzlePrices from '../CreatePuzzleScreen/hooks/usePremiumPuzzles';
import {
  InformationModal,
  InformationModalTypes,
} from '../../../../shared/Components/InformationModal';

interface ParamTypes {
  id: string;
  artistId: string;
}

type PuzzleStatusTypes = 'pending' | 'edited' | 'approved' | 'rejected' | 'disabled';

interface EditPuzzleForms {
  [EditPuzzleField.Name]: string;
  [EditPuzzleField.Keywords]: string;
  [EditPuzzleField.Collections]: { value: string; label: string }[];
  [EditPuzzleField.Publisher]: { value: string; label: string };
  [EditPuzzleField.PhysicalDistribution]: boolean;
  [EditPuzzleField.AIGenerated]: boolean;
  [EditPuzzleField.Status]: { value: PuzzleStatusTypes; label: string };
  [EditPuzzleField.PremiumTier]: { value: number; label: string };
  [EditPuzzleField.RejectMessage]: string | undefined;
  [EditPuzzleField.ForceSetPublishDate]: boolean;
}

interface ArtistOptions {
  value: string;
  label: string;
}

/**
 * Statuses meaning:
 * - loading: data is being loaded (shows overall loading screen)
 * - error: there was some error loading datam or other errors
 * - idle: ready for changes and waiting for potential submit
 * - submitting: submit request is in progress (shows loading indicator on submitt button)
 */
type ScreenState = 'idle' | 'loading' | 'error' | 'submitting';

const PUZZLE_STATUS_SELECT_OPTIONS = Object.keys(PuzzleStatusConstants).map((key) => ({
  value: key,
  label: (PuzzleStatusConstants as any)[key]?.label,
}));

const PUZZLE_STATUSES_UNAPPROVED_ARTIST_SELECT_OPTIONS = PUZZLE_STATUS_SELECT_OPTIONS.filter(
  (option) => option.value !== 'approved',
);

const PUZZLE_STATUSES_APPROVED_ARTIST_SELECT_OPTIONS = PUZZLE_STATUS_SELECT_OPTIONS.filter(
  (option) => option.value !== 'new',
);

/*
 * @todo Collections data and artists data should be moved to global state as it is effectively used on every screen except a few.
 */
const EditPuzzleScreen = () => {
  // Outer hooks - hooks data coming from outside of this component
  const { openModal, closeModal } = useModalProvider();
  const queryClient = useQueryClient(); // still required to be able to reset query on Puzzles screen.
  const history = useHistory();
  const { id } = useParams<ParamTypes>();

  const {
    docs: categoriesDocs,
    state: categoriesFetchState,
    error: categoriesFetchError,
  } = useLiveData(ALL_CATEGORIES_QUERY);

  const {
    docs: artistsDocs,
    state: artistsFetchState,
    error: artistsFetchError,
  } = useLiveData(ALL_ARTISTS_QUERY);

  const {
    state: puzzleDocumentState,
    data: puzzleDocumentData,
    error: puzzleDocumentError,
  } = useDocument(`puzzles/${id}`, true);

  const { premiumPuzzlePricesDropdownList, isLoadingPremiumPuzzlePrices } =
    usePremiumPuzzlePrices();

  const {
    handleSubmit,
    setValue,
    reset,
    control,
    trigger,
    formState: { errors, isValid: isFormValid },
  } = useForm<EditPuzzleForms>({
    schema: editPuzzleScreenSchema(id),
  });

  // Form state - some fields we need to react to value change.
  const selectedArtist: ArtistOptions = useWatch({ name: 'Publisher', control: control });
  const selectedStatus: ArtistOptions = useWatch({ name: 'status', control: control });

  // Inner state
  const [state, setState] = useState<ScreenState>('loading');

  // Screen's state-derived, refs and regular variables
  const puzzleRef = doc(collection(firestore, 'puzzles'), id);

  const requestsState = getCombinedState(
    artistsFetchState,
    categoriesFetchState,
    puzzleDocumentState,
    isLoadingPremiumPuzzlePrices ? 'loading' : 'success',
  );

  const artistOptions: ArtistOptions[] = artistsDocs.map((artistDocumentSnapshot) => ({
    value: artistDocumentSnapshot.id,
    label: artistDocumentSnapshot.data().Name,
  }));

  const categoriesOptions = useMemo(
    () =>
      categoriesDocs
        .map((snapshot) => ({
          value: snapshot.id,
          label: snapshot.data().name,
        }))
        .sort((a, b) => {
          if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
          if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
          return 0;
        }),
    [categoriesDocs],
  );

  const {
    state: artistDocumentState,
    data: artistDocumentData,
    error: artistDocumentError,
  } = useDocument(`${ARTISTS_COLLECTION_PATH}/${selectedArtist?.value}`, true);

  const imageUrl = new URL(puzzleDocumentData?.url ?? window.location.href);
  const previousVersionUrl = `${imageUrl.origin}${imageUrl.pathname}_previous${imageUrl.search}`;

  const handleDiscard = () => {
    history.goBack();
  };

  const openRejectionModal = useCallback(() => {
    openModal('reject-puzzle-modal-message', RejectionModal as React.FC, {
      rejectionModalType: 'puzzle',
      onClose: () => closeModal('reject-puzzle-modal-message'),
      onSubmit: async (message: string) => {
        setValue('rejectMessage', message as any as never);
        await trigger('status');
      },
      id: id,
    });
  }, []);

  /**
   * Function for submitting form data.
   *
   * @function
   */
  const submit = handleSubmit(async (data: EditPuzzleForms) => {
    if (!isFormValid) {
      toast.error('Form is not valid');
      return;
    }

    setState('submitting');

    const premiumTier = data.premiumTier.value ?? 0;
    if (premiumTier) {
      const res = await getDailyPuzzleByPuzzleId(id);
      if (res?.length) {
        toast.error(
          "Puzzle is/was scheduled for Daily Puzzle. Premium Puzzle can't be Daily Puzzle.",
        );
        setState('idle');
        return;
      }
    }

    try {
      const Publisher = data?.Publisher?.value;
      if (!Publisher) throw new Error("There is something wrong with puzzle's artist id.");

      //Delete this "if" when needed since this check is temporarily for now.
      if (premiumTier && !checkIfIsGotivaArtist(Publisher)) {
        toast.error('Not possible to set non-Gotiva puzzle as Premium.');
        setState('idle');
        return;
      }

      const newCollectionList = data.collections.map((collection) => collection.value);
      const primaryCollectionId = newCollectionList[0];

      const puzzleData: WithFieldValue<RecursivePartial<PuzzleDTO | VideoPuzzleDTO>> = {
        status: data.status?.value,
        keywords: data.keywords,
        collections: newCollectionList,
        Publisher,
        name: data.Name,
        physicalDistribution: data.physicalDistribution,
        aiGenerated: data.aiGenerated,
        premiumTier,
        rejectMessage:
          data.status.value === 'rejected' || data.status.value === 'edited'
            ? data.rejectMessage
            : deleteField(),
        ...(data[EditPuzzleField.ForceSetPublishDate]
          ? {
              publishedAt: serverTimestamp(),
            }
          : {}),
      };

      if (
        primaryCollectionId !== Status.Rejected &&
        data[EditPuzzleField.Status].value === Status.Rejected
      ) {
        await updateDoc(puzzleRef, '_meta.previousCollection', puzzleDocumentData?.collections[0]);
      } else if (
        primaryCollectionId !== Status.Approved &&
        data[EditPuzzleField.Status].value === Status.Approved
      ) {
        // Separated from puzzleData to avoid: "deleteField() can only appear at the top level of your update data" error.
        await updateDoc(puzzleRef, '_meta.previousCollection', deleteField());
      }

      await updateDoc(puzzleRef, puzzleData);

      toast.success(strings.notificationMessages.puzzleEditSuccess);
      await queryClient.invalidateQueries('puzzlesOrderByCollectionId');
    } catch (error) {
      console.log(error);
      toast.error(strings.toastNotificationSomethingWrong);
    }

    setState('idle');

    if (data?.status?.value === Status.Rejected && puzzleDocumentData?.status !== Status.Rejected) {
      try {
        await puzzleRejectedNotificationApi(id, data.rejectMessage);
      } catch (err) {
        console.log(err);
        toast.error('Failed to send reject notification');
      }

      try {
        await deleteObject(storageRef(storage, `puzzles/${id}/image_previous`));
      } catch (error) {
        console.log(error);
      }
    }

    if (data?.status?.value === 'approved' && puzzleDocumentData?.status !== 'approved') {
      try {
        await puzzleApprovedNotificationApi(id);
      } catch (err) {
        console.log(err);
        toast.error('Failed to send approval notification');
      }
    }
  });

  useEffect(() => {
    if (requestsState === 'loading') {
      setState('loading');
    } else if (requestsState === 'error') {
      if (puzzleDocumentError) toast.error(`Error fetching puzzle: ${puzzleDocumentError.message}`);
      if (artistDocumentError) toast.error(`Error fetching artist: ${artistDocumentError.message}`);
      if (artistsFetchError) toast.error(`Error fetching artists: ${artistsFetchError.message}`);
      if (categoriesFetchError)
        toast.error(`Error fetching categories: ${categoriesFetchError.message}`);

      setState('error');
    } else if (requestsState === 'success') {
      const puzzleCollections = (puzzleDocumentData?.collections ?? [])
        .map((collectionId: string) =>
          categoriesOptions.find((option) => option?.value === collectionId),
        )
        .filter(
          (
            collection: { value: string; label: string } | undefined,
          ): collection is {
            value: string;
            label: string;
          } => !!collection,
        );

      const publisher = artistOptions.find(
        (option) => puzzleDocumentData?.Publisher === option.value,
      );

      const status = PUZZLE_STATUS_SELECT_OPTIONS.find(
        (option) => option?.value === puzzleDocumentData?.status,
      );

      const premiumTier = premiumPuzzlePricesDropdownList.find(
        (option: any) => puzzleDocumentData?.premiumTier === option.value,
      );

      const newFormValues: Partial<EditPuzzleForms> = {
        [EditPuzzleField.Name]: puzzleDocumentData?.name,
        [EditPuzzleField.Keywords]: puzzleDocumentData?.keywords,
        [EditPuzzleField.Collections]: puzzleCollections,
        [EditPuzzleField.PhysicalDistribution]: puzzleDocumentData?.physicalDistribution,
        [EditPuzzleField.AIGenerated]: puzzleDocumentData?.aiGenerated,
        [EditPuzzleField.PremiumTier]: premiumTier ?? 0,
        [EditPuzzleField.RejectMessage]: puzzleDocumentData?.rejectMessage || '',
      };

      // These values could be undefined so we must set them conditionally:
      if (publisher) newFormValues[EditPuzzleField.Publisher] = publisher;
      if (status && isPuzzleStatusOption(status)) newFormValues[EditPuzzleField.Status] = status;

      reset(newFormValues);
      setState('idle');
    }
  }, [requestsState]);

  useEffect(() => {
    if (puzzleDocumentData && puzzleDocumentData.status === PUZZLE_STATUSES.deleted) {
      history.replace('/');
    }
  }, [puzzleDocumentData]);

  if (state === 'loading') {
    return (
      <Container>
        <Splash />
      </Container>
    );
  }

  if (state === 'error') {
    // Error page would be better.
    return (
      <Container>
        <p style={{ marginBottom: '1rem' }}>Something went wrong try refreshing the page.</p>Send
        this to developers:
        <br />
        <code>{artistDocumentError?.message || 'SOME TEST ERROR CODE'}</code>
      </Container>
    );
  }

  // Cannot approve puzzle if newly selected artist's data is loading.
  // Cannot approve puzzle if artist is not approved, only unapproved states are possible.
  const hasProhibitedState =
    state === 'submitting' ||
    artistDocumentState === 'loading' ||
    (artistDocumentData?.status !== 'approved' && selectedStatus.value === 'approved');

  const gapBetweenFields = '1.5rem';

  return (
    <Container>
      <Box width="100%">
        <Text as="h1" margin="0 0 2rem 0">
          {strings.editPuzzle}
        </Text>
        <form onSubmit={submit}>
          <FlexContainer gap="2rem">
            <FlexColumn>
              <a href={puzzleDocumentData?.url} target="_blank" rel="noreferrer">
                <Image
                  width="256px"
                  height="256px"
                  cursor="pointer"
                  src={puzzleDocumentData?.iconURL}
                  borderRadiusType="rounded"
                  alt="Picture"
                />
              </a>

              {puzzleDocumentData?.status === Status.Edited && (
                <LinkButton
                  href={previousVersionUrl}
                  target="_blank"
                  rel="noopener noreferer"
                  style={{ marginTop: '8px' }}
                >
                  Previous Version
                </LinkButton>
              )}
            </FlexColumn>

            <FlexContainer flexDirection="column" gap="2.5rem" justifyContent="space-between">
              <FlexContainer
                gap={gapBetweenFields}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Input
                  required
                  id="name"
                  label="Name"
                  placeholder="Name"
                  name="Name"
                  control={control}
                  error={errors[EditPuzzleField.Name]}
                  width="320px"
                  defaultValue={puzzleDocumentData?.name}
                />
                <Input
                  required
                  id="keywords"
                  label="Keywords"
                  placeholder="keywords"
                  name={EditPuzzleField.Keywords}
                  control={control}
                  defaultValue={puzzleDocumentData?.keywords}
                  error={errors[EditPuzzleField.Keywords]}
                  width="320px"
                />
              </FlexContainer>

              <FlexContainer gap={gapBetweenFields}>
                <SelectMultiForm
                  required
                  id="puzzle-collections"
                  label="Categories"
                  name={EditPuzzleField.Collections}
                  placeholder="Select Categories"
                  control={control}
                  width="320px"
                  values={categoriesOptions}
                  error={errors[EditPuzzleField.Collections]}
                />
              </FlexContainer>

              <FlexContainer justifyContent="space-between">
                <SelectForm
                  required
                  id="publisher"
                  label="Publisher"
                  name={EditPuzzleField.Publisher}
                  placeholder="artist"
                  control={control}
                  width="320px"
                  values={artistOptions}
                  error={errors[EditPuzzleField.Publisher]}
                />
                <FlexContainer justifyContent="space-between">
                  <SelectForm
                    required
                    id="status"
                    label={`Status ${
                      artistDocumentData?.status !== 'approved'
                        ? `- selected artist is not approved (${artistDocumentData?.status})`
                        : ''
                    }`}
                    onChange={(option) => {
                      trigger('status');
                      option?.value === 'rejected' && openRejectionModal();
                    }}
                    name={EditPuzzleField.Status}
                    placeholder="Select status"
                    control={control}
                    width={puzzleDocumentData?.status === 'rejected' ? '290px' : '320px'}
                    values={
                      artistDocumentData?.status === 'approved'
                        ? PUZZLE_STATUSES_APPROVED_ARTIST_SELECT_OPTIONS
                        : PUZZLE_STATUSES_UNAPPROVED_ARTIST_SELECT_OPTIONS
                    }
                    error={errors[EditPuzzleField.Status]}
                    disabled={artistDocumentState === 'loading'}
                  />
                  {(puzzleDocumentData?.status === 'rejected' ||
                    puzzleDocumentData?.status === 'edited') && (
                    <InformationModal
                      type={InformationModalTypes.Error}
                      title="Rejection reason"
                      content={puzzleDocumentData?.rejectMessage}
                    />
                  )}
                </FlexContainer>
              </FlexContainer>

              <FlexContainer justifyContent="space-between" gap={gapBetweenFields}>
                <FlexContainer justifyContent="space-between" gap={gapBetweenFields}>
                  <FlexContainer
                    flexDirection="row"
                    justifyContent="space-evenly"
                    alignItems="center"
                    gap="1rem"
                  >
                    <Input
                      control={control}
                      type="checkbox"
                      width="25px"
                      minWidth="25px"
                      name={EditPuzzleField.AIGenerated}
                      placeholder={strings.aiGenerated}
                    />
                    <Text size={0.8}>{'AI Generated'}</Text>
                  </FlexContainer>
                </FlexContainer>
                <SelectForm
                  required
                  width="320px"
                  placeholder={strings.selectPuzzleTier}
                  values={premiumPuzzlePricesDropdownList}
                  control={control}
                  name={EditPuzzleField.PremiumTier}
                  label="Price"
                  id="tier-select-dropdown"
                  margin="5px 0"
                />
                <FlexContainer
                  flexDirection="row"
                  justifyContent="space-evenly"
                  alignItems="center"
                  gap="1rem"
                >
                  <Input
                    control={control}
                    type="checkbox"
                    width="25px"
                    minWidth="25px"
                    name={EditPuzzleField.ForceSetPublishDate}
                    defaultValue={false}
                  />
                  <Text size={0.8}>Force set publish date</Text>
                </FlexContainer>
              </FlexContainer>

              <FlexContainer
                flexDirection="row"
                justifyContent="space-around"
                gap={gapBetweenFields}
              >
                <Button onClick={handleDiscard} variant="danger" width={200}>
                  {strings.discard}
                </Button>
                <Button
                  height={40}
                  width={200}
                  type="submit"
                  disabled={hasProhibitedState}
                  title={
                    hasProhibitedState ? 'Cannot approve puzzle while artist is not approved' : ''
                  }
                >
                  {state === 'submitting' ? (
                    <Splash color={theme.colors.white} width={25} height={25} />
                  ) : (
                    strings.submit
                  )}
                </Button>
              </FlexContainer>
            </FlexContainer>
          </FlexContainer>
        </form>
      </Box>
    </Container>
  );
};

function isPuzzleStatusOption(status: Option): status is EditPuzzleForms[EditPuzzleField.Status] {
  const statusValidation = PuzzleDTOSchema.pick(['status']);
  return typeof status.label === 'string' && isSchema({ status: status.value }, statusValidation);
}

export default EditPuzzleScreen;
